import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, ParamMap, Router, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';

import { combineLatest, distinctUntilChanged, exhaustMap, filter, map, Observable, of, switchMap, take, tap } from 'rxjs';

import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ButtonModule } from 'primeng/button';
import { ThemeService } from './services/theme.service';
import { AuthService } from './services/auth.service';
import { LaunchDarklyService } from './services/launcdarkley.service';
import { environment } from 'src/environments/environment';
import { ApolloClientService } from './services/apollo-client.service';
import { GraphqlUserDatatOutput } from './graphql/models/output/user-data-output.model';
import { AuthenticationMutations } from './graphql/mutations/authentication.mutations';
import { convertGraphqlUserDataOutputToUserData } from './graphql/mappers/user-data.mappers';
import { LoadingService } from './services/loading.serivce';
import { SpinnerComponent } from "./components/spinner/spinner.component";
import { CategoriesService } from './services/categories.service';


/**
 * Primary application component.
 */
@Component({
	selector: 'app-root',
	imports: [
		RouterOutlet, 
		ToastModule, 
		AsyncPipe, 
		ConfirmDialogModule, 
		ButtonModule, 
		SpinnerComponent
	],
	providers: [MessageService, ThemeService, ConfirmationService],
	templateUrl: './app.component.html',
	styleUrl: './app.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class AppComponent implements OnInit {
	private destroyRef = inject(DestroyRef);

	params$: Observable<ParamMap> = this.route.queryParamMap;

	navigationEnded$ = this.router.events.pipe(filter(e => e instanceof NavigationEnd));
	isLoading$ = this.loadingService.loading$;


	/**
	 *
	 * @param router To get router events.
	 * @param route To get current route.
	 * @param authService To handle authentication.
	 * @param themeService To initialize theme.
	 * @param launchDarklyService To get maintainence mode status.
	 * @param apolloClientSerivce To request user data from authentication layer.
	 * @param messageService To display error on failed authentication.
	 */
	constructor(
		private router: Router,
		private route: ActivatedRoute,
		public authService: AuthService,
		public themeService: ThemeService,
		public launchDarklyService: LaunchDarklyService,
		private apolloClientSerivce: ApolloClientService,
		private messageService: MessageService,
		private loadingService: LoadingService,
		private categoriesService: CategoriesService,
	) {}

	/**
	 * Check for updated session data on init.
	 */
	ngOnInit(): void {
		this.themeService.getTheme();
		this.categoriesService.getFpcFamilies$().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

		combineLatest({
			params: this.params$,
			navigationEnded: this.navigationEnded$,
			userData: this.authService.userData$,
		})
			.pipe(
				distinctUntilChanged((prev, curr) => prev.userData.sessionID === curr.userData.sessionID),
				exhaustMap(data => {
					if (!data.userData.sessionID) {
						const token = data.params.get(environment.params.oneTimeToken);
						if (token) {
							if (token === 'noAuth') {
								this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to sign in. No authentication token provided..' });
								return of(false);
							} else {
								return this.apolloClientSerivce.authLayerClient.pipe(
									take(1),
									switchMap(client =>
										client.mutate<{ consumeOneTimeToken: GraphqlUserDatatOutput }>({
											mutation: AuthenticationMutations.consumeOneTimeToken,
											variables: { token: token },
										}),
									),
									map(result => (result.data?.consumeOneTimeToken ? convertGraphqlUserDataOutputToUserData(result.data.consumeOneTimeToken) : undefined)),
									map(data => {
										if (data?.sessionID) {
											this.authService.updateSession(data.sessionID, data.userName, data.companyName);
											return false;
										} else {
											this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to retrieve user data. Please sign in again.' });
											return false;
										}
									}),
								);
							}
						} else {
							return of(!this.authService.signedOut);
						}
					}
					return of(false);
				}),
				tap(shouldAuthenticate => {
					if (shouldAuthenticate) {
						this.authService.login();
					}
				}),
				tap(() => this.authService.removeSpecificQueryParams(['sessionId', 'username', 'company', environment.params.oneTimeToken])),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe();
			
		document.addEventListener('touchstart', () => {
			const active = document.activeElement as HTMLElement | null;
			if (active && ['INPUT', 'TEXTAREA'].includes(active.tagName)) {
			active.blur();
			}
		});
	}
}
